import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AutoComplete, Spin } from 'antd'
import debounce from 'lodash.debounce'
import styled from 'styled-components'
import { searchPurchaseOrders } from '../../features/NewInvoicesSlice'
import {
  formatPurchaseOrderSearchOptionLabel,
  preparePurchaseOrderSearchResults,
} from '../../utils/invoicePoSearchHelpers'

const { Option } = AutoComplete

function PurchaseOrderInput({
  venueId,
  value = '',
  supplierId,
  invoiceDate,
  invoiceTotalPennies,
  excludePurchaseOrderNumbers = [],
  showOrderHints = false,
  onChange,
  placeholder = 'PO number (optional)',
}) {
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const valueRef = useRef(value)
  const excludeRef = useRef(excludePurchaseOrderNumbers)

  useEffect(() => {
    valueRef.current = value
  }, [value])

  useEffect(() => {
    excludeRef.current = excludePurchaseOrderNumbers
  }, [excludePurchaseOrderNumbers])

  const fetchPurchaseOrders = useMemo(
    () =>
      debounce(async (query, venueSupplierId) => {
        if (!venueId) {
          setOptions([])
          return
        }

        setLoading(true)
        try {
          const results = await searchPurchaseOrders(
            venueId,
            query,
            venueSupplierId,
          )
          const prepared = preparePurchaseOrderSearchResults(results, {
            invoiceDate,
            invoiceTotalPennies,
            excludePurchaseOrderNumbers: excludeRef.current,
            keepPurchaseOrderNumber: valueRef.current,
          })
          // If search misses the already-selected PO (supplier filter / lag),
          // keep it visible so Match does not look like "order does not exist".
          const currentPo = String(valueRef.current || '').trim()
          const hasCurrent = prepared.some(
            row => String(row?.purchaseOrderNumber || '').trim() === currentPo,
          )
          if (currentPo && !hasCurrent) {
            prepared.unshift({
              purchaseOrderNumber: currentPo,
              supplierTitle: '',
              orderIds: [],
            })
          }
          setOptions(prepared)
        } catch (error) {
          console.error('PO search failed:', error)
          const currentPo = String(valueRef.current || '').trim()
          setOptions(
            currentPo
              ? [
                  {
                    purchaseOrderNumber: currentPo,
                    supplierTitle: '',
                    orderIds: [],
                  },
                ]
              : [],
          )
        } finally {
          setLoading(false)
        }
      }, 300),
    [venueId, invoiceDate, invoiceTotalPennies],
  )

  useEffect(() => {
    return () => {
      fetchPurchaseOrders.cancel()
    }
  }, [fetchPurchaseOrders])

  const handleSearch = useCallback(
    searchValue => {
      fetchPurchaseOrders(searchValue, supplierId)
    },
    [fetchPurchaseOrders, supplierId],
  )

  const handleSelect = useCallback(
    (selectedValue, option) => {
      const po = option?.props?.po || option?.po
      if (!po) {
        onChange({
          purchaseOrderNumber: selectedValue,
          linkedOrderId: null,
        })
        return
      }

      onChange({
        purchaseOrderNumber: po.purchaseOrderNumber,
        linkedOrderId: po.orderIds?.[0] || null,
        venueSupplierId: po.venueSupplierId,
        supplierTitle: po.supplierTitle,
        orderedDate: po.orderedDate || null,
        deliveryDate: po.deliveryDate || null,
        sentToXero: Boolean(po.sentToXero),
        status: po.status || '',
      })
    },
    [onChange],
  )

  const handleChange = useCallback(
    inputValue => {
      onChange({
        purchaseOrderNumber: inputValue,
        linkedOrderId: null,
      })
    },
    [onChange],
  )

  return (
    <PoInputWrapper>
      <AutoComplete
        allowClear
        size='large'
        value={value}
        placeholder={placeholder}
        notFoundContent={
          loading ? (
            <Spin size='small' />
          ) : String(value || '').trim() ? (
            'Keep typing to find another PO'
          ) : (
            'No matching POs'
          )
        }
        onSearch={handleSearch}
        onSelect={handleSelect}
        onChange={handleChange}
        onFocus={() => handleSearch(value)}
      >
        {options.map(po => (
          <Option
            key={`${po.purchaseOrderNumber}-${
              po.venueSupplierId || po.supplierId
            }`}
            value={po.purchaseOrderNumber}
            po={po}
          >
            {formatPurchaseOrderSearchOptionLabel(po, {
              showHints: showOrderHints,
              invoiceDate,
            })}
          </Option>
        ))}
      </AutoComplete>
    </PoInputWrapper>
  )
}

const PoInputWrapper = styled.div`
  width: 100%;

  .ant-select-auto-complete,
  .ant-auto-complete {
    width: 100% !important;
  }
`

export default PurchaseOrderInput

export const GuidanceText = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.4;
`
